import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import Api from '../../util/api';
import { partial } from '../../util/translation/translation';
import { isPaidEvent } from './payments';

/**
 * A paid event's money side: its places held until paid (pending bookings,
 * offers, payment links) and its payments in the transactions service. Whole-
 * series payments are stored under the series' id, so for a series date
 * those are loaded too - only to show who paid; refunds of the event itself
 * (e.g. "refund all" on a cancelled date) only use the event's own.
 */
const useEventPayments = (event) => {
  const [pending, setPending] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [seriesTransactions, setSeriesTransactions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const eventId = event?.id;
  const seriesId = event?.seriesId;
  const paid = isPaidEvent(event);

  const reload = useCallback(async () => {
    if (!eventId) return;
    const p = partial('EventPayments');
    // A free event can still have payments from when it had a price, but
    // failures are only worth telling about on a paid one
    const warn = (key) => paid && toast.error(p(key));
    const [resPending, resTransactions, resSeriesTransactions] =
      await Promise.all([
        Api.events.getPendingBookings(eventId).catch(() => {
          warn('pendingLoadError');
          return null;
        }),
        Api.transactions.getTransactions(eventId).catch(() => {
          warn('paymentsLoadError');
          return null;
        }),
        seriesId
          ? Api.transactions.getTransactions(seriesId).catch(() => null)
          : Promise.resolve([]),
      ]);
    if (resPending) setPending(Array.isArray(resPending) ? resPending : []);
    if (resTransactions) {
      setTransactions(Array.isArray(resTransactions) ? resTransactions : []);
    }
    if (resSeriesTransactions) {
      setSeriesTransactions(
        Array.isArray(resSeriesTransactions) ? resSeriesTransactions : []
      );
    }
    setLoaded(true);
  }, [eventId, seriesId, paid]);

  useEffect(() => {
    reload();
  }, [reload]);

  const transactionsById = useMemo(
    () =>
      Object.fromEntries(
        [...seriesTransactions, ...transactions].map((transaction) => [
          transaction.id,
          transaction,
        ])
      ),
    [transactions, seriesTransactions]
  );

  return { pending, transactions, transactionsById, loaded, reload };
};

export default useEventPayments;
